import { useState, useMemo, useCallback } from 'react';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import PageHeader from '../components/common/PageHeader';
import Chip from '../components/common/Chip';
import Icon from '../components/common/Icon';
import { Select as FormSelect, DateField } from '../components/common/FormControls';
import { TbCalculator, TbPrinter, TbDownload, TbBuildingSkyscraper } from 'react-icons/tb';
import { useApp } from '../context/AppContext';
import { useUI } from '../context/UIContext';
import {
  computeFilingDeadlines,
  computePortfolioDeadlines,
  normalizeEntityType,
  isAgmRequired,
  parseFyEnd,
  fyLabel,
  computeAgmDeadline,
  formatDateEN,
  FORM_REGISTRY,
} from '../utils/agmDeadlineEngine';

/* ─── Display-name overrides (match prototype labels) ─────── */

const DISPLAY_CODE = {
  'AOC-4':  'AOC-4 / AOC-4 XBRL',
  'MGT-7':  'MGT-7 / MGT-7A',
  'MGT-7A': 'MGT-7 / MGT-7A',
  'AGM':    'AGM Deadline',
};

const BOTTOM_TEXT = {
  'AOC-4':        '30 days from AGM \u00B7 Penalty: \u20B9100/day',
  'AOC-4 XBRL':   '30 days from AGM \u00B7 Penalty: \u20B9100/day',
  'MGT-7':        '60 days from AGM \u00B7 Penalty: \u20B9100/day',
  'MGT-7A':       '60 days from AGM \u00B7 Penalty: \u20B9100/day',
  'ADT-1':        '15 days from AGM \u00B7 Late fee: 1\u00D7\u201312\u00D7 normal fee',
  'DIR-12':       '30 days from change \u00B7 Late fee: 2\u00D7\u201312\u00D7 normal fee + Sec 172 penalty',
  'AGM':          '6 months from FY end \u00B7 Penalty: \u20B91,00,000',
  'ITR-6':        '31 Oct (audit cases) \u00B7 Penalty: \u20B95,000 + interest',
  'DIR-3 KYC':    '30 June, every 3rd FY (from FY 2026) \u00B7 Late: \u20B95,000',
  'LLP Form 8':   '30 October \u00B7 Late fee: 1\u00D7\u201330\u00D7 filing fee (2022 slabs)',
  'LLP Form 11':  '30 May \u00B7 Late fee: 1\u00D7\u201330\u00D7 filing fee (2022 slabs)',
};

/* ─── UI helpers ──────────────────────────────────────────── */

function StatusPill({ status }) {
  if (!status || status === 'unknown') return null;
  const styles = {
    overdue:       { background: '#f2e9e6', color: '#9a564a' },
    'due-soon':    { background: '#f3ebd7', color: '#8a6a1f' },
    compliant:     { background: '#e8efe5', color: '#4a6b3f' },
    filed:         { background: '#e8efe5', color: '#4a6b3f' },
    'pending-agm': { background: '#f2eee6', color: '#8a7d66' },
  };
  const displayMap = {
    overdue:       'OVERDUE',
    'due-soon':    'Upcoming',
    compliant:     'On Track',
    filed:         'Filed',
    'pending-agm': 'Pending AGM',
  };
  const s = styles[status] || styles['pending-agm'];
  return <Chip bg={s.background} color={s.color}>{displayMap[status] || status}</Chip>;
}

function ResultCard({ code, name, section, dueDateFormatted, status, daysRemaining }) {
  const displayCode = DISPLAY_CODE[code] || code;
  const bottomText = BOTTOM_TEXT[code] || '';

  /* Status-based card background (from prototype CSS) */
  let cardBg, cardBorder;
  switch (status) {
    case 'overdue':
      cardBg = 'rgba(154,86,74,.18)'; cardBorder = 'rgba(154,86,74,.35)'; break;
    case 'due-soon':
      cardBg = 'rgba(138,106,31,.15)'; cardBorder = 'rgba(138,106,31,.35)'; break;
    case 'compliant':
    case 'filed':
      cardBg = 'rgba(74,107,63,.15)'; cardBorder = 'rgba(74,107,63,.35)'; break;
    default:
      cardBg = 'rgba(255,255,255,.07)'; cardBorder = 'rgba(255,255,255,.1)';
  }

  /* Countdown text and color */
  let countdownText = '';
  let countdownColor = '#a8c89e';
  if (daysRemaining != null) {
    if (daysRemaining < 0) {
      countdownText = `OVERDUE by ${Math.abs(daysRemaining)} days`;
      countdownColor = '#d4948a';
    } else if (daysRemaining === 0) {
      countdownText = 'DUE TODAY!';
      countdownColor = '#d4948a';
    } else if (daysRemaining <= 30) {
      countdownText = `${daysRemaining} days remaining`;
      countdownColor = '#d4b870';
    } else {
      countdownText = `${daysRemaining} days remaining`;
      countdownColor = '#a8c89e';
    }
  } else if (status === 'pending-agm') {
    countdownText = 'Pending AGM';
    countdownColor = '#d4b870';
  }

  return (
    <div className="agm-card" style={{
      background: cardBg, border: `1px solid ${cardBorder}`,
      borderRadius: 10, padding: 14, transition: 'all .2s', cursor: 'default',
    }}>
      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: '#b8973a', marginBottom: 5 }}>
        {displayCode}
      </div>
      <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,.7)', marginBottom: 7 }}>
        {name} &middot; {section}
      </div>
      <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: '#fff' }}>
        {dueDateFormatted || '\u2014'}
      </div>
      {countdownText && (
        <div style={{ fontSize: 10.5, marginTop: 3, color: countdownColor, fontWeight: 600 }}>
          {countdownText}
        </div>
      )}
      {bottomText && (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,.5)', marginTop: 5 }}>
          {bottomText}
        </div>
      )}
    </div>
  );
}

/* ─── Entity / FY options ─────────────────────────────────── */

const ENTITY_OPTIONS = [
  { value: 'pvtlimited', label: 'Private Limited' },
  { value: 'public',     label: 'Public Limited' },
  { value: 'opc',        label: 'OPC' },
  { value: 'sec8',       label: 'Section 8 / NGO' },
  { value: 'llp',        label: 'LLP' },
];

const FY_OPTIONS = [
  { value: '03-31', label: '31 March (standard)' },
  { value: '12-31', label: '31 December' },
  { value: '06-30', label: '30 June' },
  { value: '09-30', label: '30 September' },
];

/* ─── Shared input styles (from prototype .agm-date-*) ────── */

const labelStyle = {
  fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase',
  fontWeight: 700, color: 'rgba(255,255,255,.6)',
};
const inputStyle = {
  background: 'rgba(255,255,255,.1)', border: '1.5px solid rgba(255,255,255,.2)',
  borderRadius: 8, padding: '9px 14px', fontSize: 13, color: '#fff',
  fontFamily: "'Outfit', sans-serif", outline: 'none',
};

/* ═══════════════════════════════════════════════════════════ */
/*  MAIN PAGE                                                 */
/* ═══════════════════════════════════════════════════════════ */

export default function AGMCalculatorPage() {
  const { companies } = useApp();
  const { showToast } = useUI();

  /* ── Form state ─────────────────────────────────────────── */
  const [selectedCin, setSelectedCin] = useState('');
  const [coName,      setCoName]      = useState('');
  const [entityType,  setEntityType]  = useState('pvtlimited');
  const [agmDate,     setAgmDate]     = useState('');
  const [fyEnd,       setFyEnd]       = useState('03-31');
  const [results,     setResults]     = useState(null);
  const [showMulti,   setShowMulti]   = useState(false);

  /* ── Reference-table rows (auto-derived from today + selected FY end) ── */
  const referenceRows = useMemo(() => {
    const fyEndDate = parseFyEnd(fyEnd, new Date());
    const agm       = computeAgmDeadline(fyEndDate);
    const fmt       = (d) => formatDateEN(d);

    const fromReg = (key, purpose, remarks, penaltyOverride) => {
      const f   = FORM_REGISTRY[key];
      const due = f.computeDue(agm, fyEndDate);
      return [f.code, purpose, fmt(due), remarks, penaltyOverride ?? f.penalty.note];
    };

    // GSTR-9: 31 Dec of FY-end calendar year (standard Apr–Mar GST FY)
    const gstr9DueYear = fyEndDate.getFullYear();
    // DPT-3: 30 June of FY-end calendar year (90 days after 31 Mar snapshot)
    const dpt3DueYear  = fyEndDate.getFullYear();

    // Shared adjudication-penalty string for AOC-4 / MGT-7 / MGT-7A
    const filingPlusAdj = (section) =>
      `₹100/day filing fee + ${section}: ₹10K + ₹100/day, max ₹2L co. / ₹50K off.`;

    return [
      ['AGM', 'Annual General Meeting', fmt(agm),
        `6 months from FY end (${fmt(fyEndDate)})`, '₹1,00,000 + ₹5,000/day'],

      fromReg('aoc4',    'Financial Statements',
        `If AGM on ${fmt(agm)} → Due ${fmt(FORM_REGISTRY.aoc4.computeDue(agm, fyEndDate))}`,
        filingPlusAdj('Sec 137(3)')),
      fromReg('mgt7',    'Annual Return (Pvt Ltd)',
        `If AGM on ${fmt(agm)} → Due ${fmt(FORM_REGISTRY.mgt7.computeDue(agm, fyEndDate))}`,
        filingPlusAdj('Sec 92(5)')),
      fromReg('mgt7a',   'Annual Return (OPC/Small Co.)', 'Small company alternate form',
        filingPlusAdj('Sec 92(5)')),
      fromReg('adt1',    'Auditor Appointment',           'On appointment / re-appointment (5-yr term)'),
      fromReg('dir3kyc', 'Director KYC',                  'All DINs · once every 3rd FY since FY 2026 (was annual)'),
      fromReg('itr6',    'Income Tax Return',             'Tax Audit (3CA/3CB+3CD) due 30 Sep; ITR-6 due 31 Oct'),

      ['DPT-3',  'Return of Deposits',
        `30 Jun ${dpt3DueYear}`, 'Outstanding loans/advances on 31 Mar',  '₹5,000 + ₹500/day'],
      ['GSTR-9', 'GST Annual Return',
        `31 Dec ${gstr9DueYear}`, fyLabel(fyEndDate),                  '₹50/100/200/day (turnover-based, cap 0.04–0.5%)'],
      ['PAS-6',  'Reconciliation of Share Capital',
        '60 days from half-year end', 'Unlisted public & non-small pvt cos.', '₹100/day'],
      ['MSME-1', 'Outstanding dues to MSME',
        'Apr 30 / Oct 31', 'Half-yearly',                              '₹20,000 + ₹1,000/day (max ₹3L)'],
    ];
  }, [fyEnd]);

  const referenceFyLabel = useMemo(
    () => fyLabel(parseFyEnd(fyEnd, new Date())),
    [fyEnd],
  );

  /* ── Company selector (auto-fill from portfolio) ────────── */
  const handleCompanySelect = useCallback((cin) => {
    setSelectedCin(cin);
    setResults(null);
    setShowMulti(false);
    if (!cin) return;
    const co = companies.find((c) => String(c.cin || '').toUpperCase() === cin.toUpperCase());
    if (!co) return;
    setCoName(co.name || '');
    setEntityType(normalizeEntityType(co.type));
    if (co.financialYearEnd) {
      const fy = co.financialYearEnd.toLowerCase();
      if (fy.includes('march') || fy === '03-31')      setFyEnd('03-31');
      else if (fy.includes('dec') || fy === '12-31')    setFyEnd('12-31');
      else if (fy.includes('jun') || fy === '06-30')    setFyEnd('06-30');
      else if (fy.includes('sep') || fy === '09-30')    setFyEnd('09-30');
      else setFyEnd('03-31');
    }
    if (co.lastAgm) {
      const d = new Date(co.lastAgm);
      if (!isNaN(d.getTime())) setAgmDate(d.toISOString().split('T')[0]);
    }
  }, [companies]);

  /* ── Calculate ──────────────────────────────────────────── */
  const calculate = useCallback(() => {
    const entity = normalizeEntityType(entityType);
    const result = computeFilingDeadlines(agmDate || null, entity, fyEnd);
    setResults(result);
    setShowMulti(false);
    showToast('All due dates calculated!');
  }, [agmDate, entityType, fyEnd, showToast]);

  /* ── Multi-company summary ──────────────────────────────── */
  const portfolioSummary = useMemo(() => {
    if (!showMulti || !companies.length) return null;
    return computePortfolioDeadlines(companies);
  }, [showMulti, companies]);

  /* ── Derived ────────────────────────────────────────────── */
  const entity  = normalizeEntityType(entityType);
  const agmReq  = isAgmRequired(entity);
  const hasCos  = companies.length > 0;
  const allDeadlines = results
    ? [...results.deadlines, ...(results.agmDeadline ? [results.agmDeadline] : [])]
    : [];

  /* ── Print Calendar (opens styled print window) ─────────── */
  const printCalendar = useCallback(() => {
    if (!allDeadlines.length) return;
    const name = coName || 'Company';
    const rowsHtml = allDeadlines.map((r) => {
      const diff = r.daysRemaining;
      const stat = diff != null && diff < 0 ? 'OVERDUE' : diff != null && diff <= 7 ? 'Due Soon' : diff != null && diff <= 30 ? 'Upcoming' : 'On Track';
      const days = diff != null ? (diff < 0 ? `${Math.abs(diff)}d overdue` : `${diff} days`) : '\u2014';
      return `<tr><td><strong>${r.code}</strong></td><td>${r.name}</td><td>${r.section}</td><td><strong>${r.dueDateFormatted}</strong></td><td>${days}</td><td style="color:#9a564a">${r.penaltyNote}</td><td>${stat}</td></tr>`;
    }).join('');
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>AGM Filing Calendar \u2014 ${name}</title>
<style>body{font-family:Georgia,serif;font-size:12px;padding:30px}h2{color:#145f5f}
table{width:100%;border-collapse:collapse}th,td{padding:9px 12px;border:1px solid #ccc;text-align:left}th{background:#f5f5f5;font-size:11px}
@media print{@page{margin:1.5cm}}</style></head><body>
<h2>AGM Filing Calendar \u2014 ${name}</h2>
<table><thead><tr><th>Form / Compliance</th><th>Purpose</th><th>Section / Rule</th><th>Due Date</th><th>Days Remaining</th><th>Penalty</th><th>Status</th></tr></thead>
<tbody>${rowsHtml}</tbody></table></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  }, [allDeadlines, coName]);

  /* ── Download CSV ───────────────────────────────────────── */
  const downloadCSV = useCallback(() => {
    if (!allDeadlines.length) return;
    const lines = ['Form,Purpose,Section,Due Date,Days Remaining,Penalty'];
    allDeadlines.forEach((d) => {
      const days = d.daysRemaining != null ? (d.daysRemaining < 0 ? `${Math.abs(d.daysRemaining)}d overdue` : `${d.daysRemaining} days`) : '\u2014';
      lines.push([d.code, d.name, d.section, d.dueDateFormatted, days, d.penaltyNote].map((c) => `"${String(c).replace(/"/g, "'")}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'AGM_Filing_Calendar.csv'; a.click();
    URL.revokeObjectURL(url);
    showToast('Calendar exported as CSV!');
  }, [allDeadlines, showToast]);

  /* ═════════════════════════════════════════════════════════ */
  /*  RENDER                                                  */
  /* ═════════════════════════════════════════════════════════ */

  return (
    <AppLayout pageTitle="AGM Due Date Calculator">

      {/* Injected hover + compact styles */}
      <style>{`
        .agm-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,.25); border-color: rgba(184,151,58,.4) !important; }
        .agm-tbl tr:hover td { background: #f8f5ef; }
        .agm-tbl th { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:1px; color:#7a7060; padding:10px 14px; text-align:left; background:#f8f5ef; border-bottom:1px solid #ddd6c6; white-space:nowrap; }
        .agm-tbl td { padding:9px 14px; font-size:11.5px; border-bottom:1px solid #f0ece2; }
        .agm-tbl tr:last-child td { border-bottom:none; }
      `}</style>

      {/* ─── Page Header ────────────────────────────────────── */}
      <PageHeader
        title="AGM Due Date Calculator & Filing Deadline Tracker"
        subtitle={agmReq
          ? 'Enter AGM date \u00B7 Auto-calculates AOC-4 (30 days), MGT-7/7A (60 days) & other statutory filing deadlines'
          : 'LLP / OPC \u00B7 Calculates Form 8, Form 11, DIR-3 KYC & ITR deadlines (no AGM required)'}
      />

      {/* ─── Hero Section (inputs + result cards) ───────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #0a3d3d, #08111f)',
        borderRadius: 14, padding: 24, marginBottom: 22,
        color: '#fff', position: 'relative', overflow: 'hidden',
      }}>
        {/* Watermark */}
        <div style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', opacity: 0.07, pointerEvents: 'none' }}>
          <Icon name={agmReq ? 'calendar' : 'documents'} size={70} />
        </div>

        {/* Title */}
        <div className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: '#b8973a', marginBottom: 5 }}>
          <Icon name={agmReq ? 'calendar' : 'documents'} size={20} />
          {agmReq ? 'AGM Filing Deadline Engine' : 'Compliance Filing Engine'}
        </div>
        <div style={{ fontSize: 12.5, opacity: 0.65, marginBottom: 18 }}>
          {agmReq
            ? 'As per Companies Act, 2013 \u2014 Sec 137 (AOC-4: 30 days) \u00B7 Sec 92 (MGT-7: 60 days). Enter your AGM date and get all critical deadlines instantly.'
            : 'LLP Act \u00B7 Form 8 within 30 days of 6 months from FY end \u00B7 Form 11 within 60 days of FY close.'}
        </div>

        {/* ── Input Row ─────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>

          {/* Company Name */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={labelStyle}>Company Name</div>
            <input
              value={coName}
              onChange={(e) => setCoName(e.target.value)}
              placeholder="Company Name"
              style={{ ...inputStyle, width: 220 }}
            />
          </div>

          {/* Entity Type */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={labelStyle}>Entity Type</div>
            <div style={{ width: 160 }}>
              <FormSelect
                tone="dark"
                size="sm"
                value={entityType}
                options={ENTITY_OPTIONS}
                onChange={(v) => { setEntityType(v); setResults(null); }}
              />
            </div>
          </div>

          {/* Date of AGM */}
          {agmReq && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={labelStyle}>Date of AGM</div>
              <div style={{ width: 178 }}>
                <DateField
                  tone="dark"
                  size="sm"
                  value={agmDate}
                  onChange={(v) => { setAgmDate(v); setResults(null); }}
                  placeholder="Select AGM date"
                />
              </div>
            </div>
          )}

          {/* Financial Year End */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={labelStyle}>Financial Year End</div>
            {/* Wider than the old native select: the custom trigger carries a
                chevron, which would otherwise clip "31 March (standard)". */}
            <div style={{ width: 178 }}>
              <FormSelect
                tone="dark"
                size="sm"
                value={fyEnd}
                options={FY_OPTIONS}
                onChange={(v) => { setFyEnd(v); setResults(null); }}
              />
            </div>
          </div>

          {/* Calculate Button */}
          <Button variant="gold" className="self-end" leftIcon={<TbCalculator size={15} />} onClick={calculate}>
            Calculate
          </Button>
        </div>

        {/* ── Result Cards (inside hero, 3-column grid) ─────── */}
        {results && !showMulti && allDeadlines.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 14, marginTop: 14,
          }}>
            {allDeadlines.map((d) => (
              <ResultCard key={d.key} {...d} />
            ))}
          </div>
        )}
      </div>

      {/* ─── Portfolio selector (subtle, below hero) ────────── */}
      {hasCos && !showMulti && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18 }} className="flex-wrap">
          <div style={{ minWidth: 240 }}>
            <FormSelect
              size="sm"
              value={selectedCin}
              onChange={handleCompanySelect}
              placeholder="Select from portfolio\u2026"
              options={[{ value: '', label: 'Select from portfolio\u2026' }, ...companies.map((c) => ({ value: c.cin, label: c.name || c.cin }))]}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<TbBuildingSkyscraper size={15} />}
            onClick={() => { setShowMulti(true); setResults(null); }}
          >
            All Companies
          </Button>
        </div>
      )}

      {/* ─── Total Penalty Exposure Banner ──────────────────── */}
      {results && !showMulti && (() => {
        const overdueItems = allDeadlines.filter((d) => d.status === 'overdue');
        const total = results.totalPenalty || 0;
        if (total <= 0 && overdueItems.length === 0) return null;
        return (
          <div style={{
            background: total > 0 ? '#f2e9e6' : '#e8efe5',
            border: `1.5px solid ${total > 0 ? '#e6d2cb' : '#d3e2cb'}`,
            borderRadius: 12, padding: 18, marginBottom: 18,
            display: 'flex', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap',
          }}>
            {/* Left — big total */}
            <div style={{ minWidth: 180 }}>
              <div className="flex items-center gap-1.5" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: total > 0 ? '#9a564a' : '#4a6b3f', marginBottom: 4 }}>
                <Icon name={total > 0 ? 'warning' : 'success'} size={12} /> {total > 0 ? 'Total Penalty Exposure' : 'No Penalties'}
              </div>
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 700, color: total > 0 ? '#9a564a' : '#4a6b3f' }}>
                {'\u20B9'}{total.toLocaleString('en-IN')}
              </div>
              {total > 0 && (
                <div style={{ fontSize: 11, color: '#9a564a', marginTop: 4 }}>
                  File immediately to stop penalties from accumulating
                </div>
              )}
            </div>
            {/* Right — per-form breakdown */}
            {overdueItems.length > 0 && (
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1.5, color: '#7a7060', marginBottom: 6 }}>
                  Breakdown by Form
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {overdueItems.map((d) => (
                    <div key={d.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontWeight: 700, color: '#333' }}>{d.code}</span>
                        <span style={{ color: '#999' }}>{Math.abs(d.daysRemaining)}d overdue</span>
                      </div>
                      <span style={{ fontWeight: 700, color: '#9a564a' }}>
                        {'\u20B9'}{(d.penalty?.total || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ─── Filing Calendar Table ──────────────────────────── */}
      {results && !showMulti && (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5',
          boxShadow: '0 2px 16px rgba(8,17,31,.07)', marginBottom: 20, overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '16px 20px', borderBottom: '1px solid #e5e5e5',
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          }}>
            <div className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 700, flex: 1 }}>
              <Icon name="documents" size={17} style={{ color: '#145f5f' }} /> Complete Filing Calendar {coName ? `\u2014 ${coName}` : '\u2014 Company'}
            </div>
            <Button variant="ghost" size="sm" iconOnly style={{ color: '#3a4a66' }} title="Print calendar" leftIcon={<TbPrinter size={16} />} onClick={printCalendar} />
            <Button variant="ghost" size="sm" iconOnly style={{ color: '#145f5f' }} title="Download CSV" leftIcon={<TbDownload size={16} />} onClick={downloadCSV} />
          </div>

          {/* Table */}
          <div style={{ overflowX: 'auto' }}>
            <table className="agm-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Form / Compliance', 'Purpose', 'Section / Rule', 'Due Date', 'Days Remaining', 'Penalty', 'Status'].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allDeadlines.map((row) => {
                  const diff = row.daysRemaining;
                  const daysColor = diff != null && diff < 0 ? '#9a564a' : diff != null && diff <= 30 ? '#8a6a1f' : '#4a6b3f';
                  const daysDisplay = diff != null
                    ? (diff < 0 ? `${Math.abs(diff)}d overdue` : `${diff} days`)
                    : '\u2014';
                  return (
                    <tr key={row.key}>
                      <td style={{ fontWeight: 700, color: '#333', whiteSpace: 'nowrap' }}>{row.code}</td>
                      <td style={{ color: '#666' }}>{row.name}</td>
                      <td style={{ color: '#666', whiteSpace: 'nowrap' }}>{row.section}</td>
                      <td style={{ fontWeight: 700, color: '#333', whiteSpace: 'nowrap' }}>{row.dueDateFormatted}</td>
                      <td style={{ fontWeight: 600, color: daysColor, whiteSpace: 'nowrap' }}>{daysDisplay}</td>
                      <td style={{ color: '#9a564a', whiteSpace: 'nowrap' }}>{row.penaltyNote}</td>
                      <td><StatusPill status={row.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Important Notes */}
          <div style={{
            margin: 14, padding: 12, background: '#e4f5f3',
            borderRadius: 8, border: '1px solid #c0e8e4',
            fontSize: 11.5, color: '#145f5f',
          }}>
            <strong className="inline-flex items-center gap-1 align-middle"><Icon name="info" size={13} /> Important Notes:</strong><br />
            {agmReq ? (
              <>
                {'\u2022'} AOC-4 due date = 30 days from the date of AGM (Section 137 of Companies Act, 2013)<br />
                {'\u2022'} MGT-7/MGT-7A due date = 60 days from the date of AGM (Section 92 of Companies Act, 2013)<br />
                {'\u2022'} OPC and Small Companies file MGT-7A instead of MGT-7<br />
                {'\u2022'} If AGM is held before the statutory deadline, the 30/60 day count starts from the actual AGM date<br />
                {'\u2022'} Penalty for delayed filing: {'\u20B9'}100 per day (no max cap) for most ROC forms
              </>
            ) : (
              <>
                {'\u2022'} LLP Form 8 is due within 30 days from the end of 6 months from FY close<br />
                {'\u2022'} LLP Form 11 is due within 60 days from FY close<br />
                {'\u2022'} DIR-3 KYC is due 30 June of every third FY (triennial since FY 2026; first cycle 30 Jun 2028)<br />
                {'\u2022'} Late fee for LLP forms: 1{'\u00D7'}{'\u2013'}30{'\u00D7'} the normal filing fee by delay band (LLP Amendment Rules 2022; small LLPs pay half), +{'\u20B9'}10{'\u2013'}20/day beyond 360 days
              </>
            )}
          </div>
        </div>
      )}

      {/* ─── Multi-Company Summary ──────────────────────────── */}
      {showMulti && portfolioSummary && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 700, color: '#333' }}>
              <Icon name="company" size={17} style={{ color: '#145f5f' }} /> Portfolio Compliance Summary &mdash; {portfolioSummary.stats.total} Companies
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowMulti(false)}>
              {'\u2190'} Single Company
            </Button>
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Overdue / Critical', value: portfolioSummary.stats.overdue, color: '#9a564a', bg: '#f2e9e6' },
              { label: 'Due Soon', value: portfolioSummary.stats.dueSoon, color: '#8a6a1f', bg: '#f3ebd7' },
              { label: 'Compliant', value: portfolioSummary.stats.compliant, color: '#4a6b3f', bg: '#e8efe5' },
              { label: 'Pending AGM', value: portfolioSummary.stats.pending, color: '#8a7d66', bg: '#f2eee6' },
            ].map((s) => (
              <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: 16, border: '1px solid #ddd6c6' }}>
                <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700, color: s.color }}>{s.label}</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 700, color: s.color, marginTop: 4 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Per-company table */}
          <div style={{
            background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5',
            boxShadow: '0 2px 16px rgba(8,17,31,.07)', overflow: 'hidden',
          }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e5e5' }}>
              <span className="inline-flex items-center gap-2 align-middle" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 700 }}><Icon name="reporting" size={17} style={{ color: '#145f5f' }} /> Company-wise Deadlines</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="agm-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['Company', 'Entity', 'FY', 'AGM', 'Next Critical', 'Due Date', 'Deadline Status', 'Penalty'].map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {portfolioSummary.results.map((r) => {
                    const worst = [...r.deadlines, ...(r.agmDeadline ? [r.agmDeadline] : [])].find(
                      (d) => d.status === 'overdue' || d.status === 'due-soon'
                    );
                    return (
                      <tr key={r.company.cin || r.company.name}>
                        <td style={{ fontWeight: 600, color: '#333' }}>{r.company.name}</td>
                        <td style={{ color: '#666' }}>{r.entityLabel}</td>
                        <td style={{ color: '#666', whiteSpace: 'nowrap' }}>{r.fyLabel}</td>
                        <td>
                          {r.agmHeld
                            ? <span style={{ color: '#4a6b3f', fontWeight: 600 }}>Held</span>
                            : r.agmRequired
                              ? <span style={{ color: '#8a6a1f', fontWeight: 600 }}>Pending</span>
                              : <span style={{ color: '#999' }}>N/A</span>}
                        </td>
                        <td style={{ fontWeight: 600, color: '#333', whiteSpace: 'nowrap' }}>{worst?.code || r.nextCritical?.code || '\u2014'}</td>
                        <td style={{ color: '#555', whiteSpace: 'nowrap' }}>{worst?.dueDateFormatted || r.nextCritical?.dueDateFormatted || '\u2014'}</td>
                        <td><StatusPill status={worst?.status || r.nextCritical?.status} /></td>
                        <td style={{ fontWeight: 600, color: r.totalPenalty > 0 ? '#9a564a' : '#4a6b3f' }}>
                          {r.totalPenalty > 0 ? `\u20B9${r.totalPenalty.toLocaleString('en-IN')}` : '\u2014'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ─── FY Statutory Deadlines Reference ───────────────── */}
      <div style={{
        background: '#fff', borderRadius: 12, border: '1px solid #e5e5e5',
        boxShadow: '0 2px 16px rgba(8,17,31,.07)', overflow: 'hidden',
        marginTop: 18,
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e5e5' }}>
          <div className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, fontWeight: 700 }}>
            <Icon name="calendar" size={17} style={{ color: '#145f5f' }} /> {results?.fyLabel || referenceFyLabel} Statutory Deadlines Reference ({results?.entityLabel || 'Pvt Ltd'})
          </div>
          <div style={{ fontSize: 11.5, color: '#888', marginTop: 3 }}>
            Filings being prepared for this FY {'\u2014'} due over coming months
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="agm-tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Form', 'Purpose', 'Standard Due Date', 'Remarks', 'Penalty'].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {referenceRows.map((row) => (
                <tr key={row[0]}>
                  <td style={{ fontWeight: 700, color: '#333', whiteSpace: 'nowrap' }}>{row[0]}</td>
                  <td style={{ color: '#555' }}>{row[1]}</td>
                  <td style={{ color: '#555', whiteSpace: 'nowrap' }}>{row[2]}</td>
                  <td style={{ color: '#666' }}>{row[3]}</td>
                  <td style={{ color: '#9a564a', fontWeight: 600, maxWidth: 280 }}>{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '10px 20px', fontSize: 11, color: '#888', borderTop: '1px solid #f0ece2', lineHeight: 1.5, background: '#f8f5ef' }}>
          <strong>Small Company</strong> = paid-up capital ≤ ₹4 Cr <em>and</em> turnover ≤ ₹40 Cr (Sec 2(85)). Small Cos. file MGT-7A instead of MGT-7 and get 50% penalty relief under Sec 446B.
        </div>
      </div>

      {/* ─── Empty state ────────────────────────────────────── */}
      {!results && !showMulti && (
        <div style={{ textAlign: 'center', padding: '60px 0', fontSize: 13, color: '#aaa' }}>
          {agmReq
            ? <>Enter the AGM date and click <strong>Calculate</strong> to see all filing deadlines.</>
            : <>Click <strong>Calculate</strong> to see all LLP filing deadlines for the selected financial year.</>}
          {hasCos && <div style={{ marginTop: 8 }}>Or click <strong>All Companies</strong> for a portfolio-wide compliance summary.</div>}
        </div>
      )}
    </AppLayout>
  );
}
